import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import useRole from "../../auth/useRole";
import useActiveBusinessMembership from "../../hooks/useActiveBusinessMembership";
import useStore from "../../store/useStore";
import { ACCOUNT_TYPES } from "../../services/profileService";
import {
  BUSINESS_HUB_PATH,
  PLAYLISTS_PATH,
  resolveBusinessGate,
} from "../../services/postAuthRouting";
import BusinessRequiredState from "../business/BusinessRequiredState";

function featureLabelFor(pathname) {
  if (pathname.startsWith(PLAYLISTS_PATH)) return "Playlists";
  if (pathname.startsWith("/inssa-live")) return "Inssa Live";
  if (pathname.startsWith("/moment-maker")) return "Moment Maker";
  return "Time Capsules";
}

export default function RequireCollectionAccess() {
  const { user, loading } = useAuth() || {};
  const { isAdmin } = useRole();
  const { userProfile } = useStore();
  const {
    membershipLoading,
    hasActiveBusinessContext,
    isActiveBusinessProfileLive,
    canAccessCollections,
    canAccessTimeCapsules,
  } = useActiveBusinessMembership();
  const loc = useLocation();

  if (loading) return null;
  if (!user) return <Navigate to="/signin" state={{ from: loc }} replace />;
  if (isAdmin) return <Outlet />;

  const accountType = userProfile?.accountType;
  const hasAccountType = Object.values(ACCOUNT_TYPES).includes(accountType);
  if (!hasAccountType) {
    return <Navigate to="/account-type" state={{ from: loc }} replace />;
  }

  if (membershipLoading && hasActiveBusinessContext) return null;

  const gate = resolveBusinessGate({
    accountType,
    hasActiveBusinessContext,
    isActiveBusinessProfileLive,
  });
  if (!gate.allowed) {
    return (
      <BusinessRequiredState
        reason={gate.reason}
        featureLabel={featureLabelFor(loc.pathname)}
        nextPath={`${loc.pathname}${loc.search || ""}`}
      />
    );
  }

  // Business is live, but this member's scope no longer includes this feature.
  const isCollections = loc.pathname.startsWith(PLAYLISTS_PATH);
  const hasScopeAccess = isCollections ? canAccessCollections : canAccessTimeCapsules;
  if (!hasScopeAccess) {
    return <Navigate to={BUSINESS_HUB_PATH} state={{ from: loc, accessRevoked: true }} replace />;
  }

  return <Outlet />;
}
